import FilePicker from '@/components/atoms/FilePicker';
import { CloseIcon } from '@chakra-ui/icons';
import { Box, Center, HStack, IconButton, Image, Text } from '@chakra-ui/react';
import { ImageUp } from 'lucide-react';
import React, { useRef, useState } from 'react';

type Props = {
  // Existing remote image (when editing) shown until a new file is chosen.
  existingUrl?: string | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  // Backend rejects undersized images on upload.
  fileMinWidthPx?: number;
  fileMinHeightPx?: number;
  fileSizeLimitInMBs?: number;
};

// "Upload Image" affordance: an outlined button when empty, the image with a remove
// (×) control once chosen — matching the creator article-content design.
const ImagePicker = ({
  existingUrl,
  file,
  onFileChange,
  fileMinWidthPx = 400,
  fileMinHeightPx = 400,
  fileSizeLimitInMBs = 5,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const shownImage = preview || (file ? undefined : existingUrl) || undefined;

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileChange(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box width="100%" display="flex" justifyContent="center" alignItems="center" py={'10px'}>
      {shownImage ? (
        <Box position="relative" width="100%" borderRadius="12px" overflow="hidden">
          <Image src={shownImage} alt="Section image" width="100%" maxHeight="320px" objectFit="cover" />
          <IconButton
            aria-label="Remove image"
            icon={<CloseIcon boxSize={2.5} />}
            size="sm"
            position="absolute"
            top="10px"
            right="10px"
            borderRadius="full"
            backgroundColor="whiteAlpha.800"
            _hover={{ backgroundColor: 'white' }}
            onClick={clear}
          />
        </Box>
      ) : (
        <Center
          as="button"
          type="button"
          onClick={() => inputRef.current?.click()}
          width="200px"
          border="1px solid"
          borderColor="text.mediumBlue"
          borderRadius="10px"
          paddingY="12px"
          backgroundColor="white"
          _hover={{ backgroundColor: 'background.lightPurple' }}
          transition="background-color 0.18s ease"
        >
          <HStack gap="8px" color="text.mediumBlue">
            <ImageUp size={18} />
            <Text fontSize="14px" fontWeight={700}>
              Upload Image
            </Text>
          </HStack>
        </Center>
      )}

      <FilePicker
        inputRef={inputRef}
        selectedFile={file}
        setSelectedFile={onFileChange}
        setPreview={setPreview}
        accept="image/*"
        fileMinWidthPx={fileMinWidthPx}
        fileMinHeightPx={fileMinHeightPx}
        fileSizeLimitInMBs={fileSizeLimitInMBs}
      />
    </Box>
  );
};

export default ImagePicker;
